import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import CoverMediaEditor from '../components/CoverMediaEditor';
import ProductPicker from '../components/ProductPicker';
import ShopMediaPreview from '../components/ShopMediaPreview';
import ShopProductFields from '../components/ShopProductFields';
import {
  buildStickerPackPayload,
  CandidateProduct,
  COVER_SLOT_TYPES,
  emptyShopProductForm,
  Environment,
  formFromProduct,
  ShopMediaAsset,
  ShopProductForm,
  STICKER_PACK_LIMITS,
  STICKER_PACK_TYPE,
  STICKER_TYPE,
  THEMES_DECOR_CATEGORY,
} from '../utils/premiumBundleRules';
import { authHeaders, deleteWalletShopProduct, fetchProductsByType, saveWalletShopProduct } from '../utils/walletShopApi';

type StickerItem = { ProductKey: string; Name?: string; MediaAssets?: ShopMediaAsset[] };

/**
 * Create / edit a sticker pack (`sticker_pack`) grouping existing `sticker` products. A premium
 * bundle's sticker-pack component is one of these. Route: /products/sticker-packs/new and
 * /products/sticker-packs/edit?productKey=…&env=…
 */
const StickerPackEditor: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productKeyParam = (searchParams.get('productKey') || '').trim().toUpperCase();
  const isUpdateMode = !!productKeyParam;
  const envParam: Environment = (searchParams.get('env') || '').toLowerCase() === 'dev' ? 'dev' : 'prod';

  const [form, setForm] = useState<ShopProductForm>(() => ({ ...emptyShopProductForm(envParam), ProductKey: productKeyParam }));
  const [stickerKeys, setStickerKeys] = useState<string[]>([]);
  const [resolvedStickers, setResolvedStickers] = useState<StickerItem[]>([]);
  const [mediaAssets, setMediaAssets] = useState<ShopMediaAsset[]>([]);
  const [stickers, setStickers] = useState<CandidateProduct[]>([]);
  const [loadingStickers, setLoadingStickers] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(isUpdateMode);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const patchForm = (patch: Partial<ShopProductForm>) => setForm((prev) => ({ ...prev, ...patch }));
  const backToList = useCallback(
    (env: Environment) => navigate(`/products?category=${THEMES_DECOR_CATEGORY}&type=${STICKER_PACK_TYPE}&env=${env}`),
    [navigate]
  );

  useEffect(() => {
    const auth = authHeaders();
    if ('error' in auth) {
      toast.error(auth.error);
      setLoadingProduct(false);
      return;
    }
    let cancelled = false;
    fetchProductsByType<Record<string, any>>(auth.headers, STICKER_PACK_TYPE, envParam)
      .then((packs) => {
        if (cancelled) return;
        if (!isUpdateMode) {
          const maxOrder = packs.reduce((max, p) => Math.max(max, Number(p.DisplayOrder) || 0), 0);
          setForm((prev) => (prev.DisplayOrder ? prev : { ...prev, DisplayOrder: String(maxOrder + 1) }));
          return;
        }
        const existing = packs.find((p) => p.ProductKey === productKeyParam);
        if (!existing) {
          setNotFound(true);
          return;
        }
        setForm(formFromProduct(existing, envParam));
        setStickerKeys(existing.StickerProductKeys || (existing.StickerItems || []).map((s: StickerItem) => s.ProductKey));
        setResolvedStickers(existing.StickerItems || []);
        const assets: ShopMediaAsset[] = existing.MediaAssets || [];
        setMediaAssets(assets.filter((a) => (COVER_SLOT_TYPES as string[]).includes(a.Type)));
      })
      .catch((error) => !cancelled && toast.error((error as Error).message || 'Failed to load sticker packs'))
      .finally(() => !cancelled && setLoadingProduct(false));
    return () => {
      cancelled = true;
    };
  }, [envParam, isUpdateMode, productKeyParam]);

  useEffect(() => {
    const auth = authHeaders();
    if ('error' in auth) return;
    let cancelled = false;
    setLoadingStickers(true);
    fetchProductsByType<CandidateProduct>(auth.headers, STICKER_TYPE, form.Environment)
      .then((list) => !cancelled && setStickers(list.map((s) => ({ ...s, Type: s.Type || STICKER_TYPE }))))
      .catch((error) => !cancelled && toast.error((error as Error).message || 'Failed to load stickers'))
      .finally(() => !cancelled && setLoadingStickers(false));
    return () => {
      cancelled = true;
    };
  }, [form.Environment]);

  const stickerLookup = useMemo(() => new Map(stickers.map((s) => [s.ProductKey, s])), [stickers]);
  const resolvedLookup = useMemo(() => new Map(resolvedStickers.map((s) => [s.ProductKey, s])), [resolvedStickers]);
  const selectedSet = useMemo(() => new Set(stickerKeys), [stickerKeys]);

  const { payload, issues } = useMemo(
    // Existence is only checked once the sticker list has loaded.
    () => buildStickerPackPayload(form, stickerKeys, mediaAssets, loadingStickers ? undefined : stickerLookup),
    [form, stickerKeys, mediaAssets, stickerLookup, loadingStickers]
  );

  const toggleSticker = (candidate: CandidateProduct) => {
    setStickerKeys((prev) => {
      if (prev.includes(candidate.ProductKey)) return prev.filter((key) => key !== candidate.ProductKey);
      if (prev.length >= STICKER_PACK_LIMITS.max) {
        toast.error(`A sticker pack holds at most ${STICKER_PACK_LIMITS.max} stickers.`);
        return prev;
      }
      return [...prev, candidate.ProductKey];
    });
  };

  const handleSave = async () => {
    if (issues.length) {
      toast.error(issues[0]);
      return;
    }
    const auth = authHeaders();
    if ('error' in auth) {
      toast.error(auth.error);
      return;
    }
    setSaving(true);
    try {
      const json = await saveWalletShopProduct(auth.headers, payload, isUpdateMode);
      toast.success(json.message || 'Sticker pack saved');
      backToList(form.Environment);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to save sticker pack');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete sticker pack ${form.ProductKey} (${form.Environment})? The stickers themselves are not deleted.`)) return;
    const auth = authHeaders();
    if ('error' in auth) {
      toast.error(auth.error);
      return;
    }
    setDeleting(true);
    try {
      const json = await deleteWalletShopProduct(auth.headers, form.ProductKey, form.Environment);
      toast.success(json.message || 'Sticker pack deleted');
      backToList(form.Environment);
    } catch (error) {
      // e.g. the backend refuses while an active premium bundle references this pack.
      toast.error((error as Error).message || 'Failed to delete sticker pack');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />
      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between gap-4'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>{isUpdateMode ? 'Update Sticker Pack' : 'Create Sticker Pack'}</h1>
            <p className='text-sm text-gray-400'>
              Groups {STICKER_PACK_LIMITS.min}–{STICKER_PACK_LIMITS.max} existing stickers into one product. Use it on its own or as a
              premium bundle's sticker pack.
            </p>
          </div>
          <button
            onClick={() => backToList(form.Environment)}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 shrink-0'
          >
            Back to Assets
          </button>
        </div>

        {loadingProduct ? (
          <div className='text-sm text-gray-400'>Loading sticker pack...</div>
        ) : notFound ? (
          <div className='p-6 bg-white/5 border border-white/10 rounded-2xl text-sm text-gray-300'>
            Sticker pack {productKeyParam} was not found in {envParam}.
          </div>
        ) : (
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            <div className='space-y-6'>
              <section className='bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4'>
                <div>
                  <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 1</p>
                  <h2 className='text-lg font-semibold text-white'>Stickers ({stickerKeys.length})</h2>
                </div>
                {stickerKeys.length > 0 && (
                  <div className='grid grid-cols-4 sm:grid-cols-6 gap-2'>
                    {stickerKeys.map((key) => {
                      const sticker = stickerLookup.get(key) || resolvedLookup.get(key);
                      const missing = !loadingStickers && !stickerLookup.has(key);
                      return (
                        <div
                          key={key}
                          className={`relative rounded-lg border p-1 ${missing ? 'border-red-400/60' : 'border-white/10'} bg-black/20`}
                          title={`${sticker?.Name || key} (${key})${missing ? ` — not found in ${form.Environment}` : ''}`}
                        >
                          <ShopMediaPreview assets={sticker?.MediaAssets} size={52} showVideo={false} />
                          <button
                            type='button'
                            aria-label={`Remove ${key}`}
                            onClick={() => setStickerKeys((prev) => prev.filter((k) => k !== key))}
                            className='absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] leading-none'
                          >
                            ✕
                          </button>
                          <div className='text-[9px] text-gray-400 truncate mt-0.5'>{key}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <ProductPicker
                  multi
                  candidates={stickers}
                  loading={loadingStickers}
                  selectedKeys={selectedSet}
                  onPick={toggleSticker}
                  emptyText={`No stickers in ${form.Environment}.`}
                  maxHeight={360}
                />
              </section>

              <section className='bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4'>
                <div>
                  <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 2</p>
                  <h2 className='text-lg font-semibold text-white'>Cover</h2>
                </div>
                <CoverMediaEditor productKey={form.ProductKey} mediaAssets={mediaAssets} onChange={setMediaAssets} />
              </section>
            </div>

            <section className='bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4 h-fit'>
              <div>
                <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 3</p>
                <h2 className='text-lg font-semibold text-white'>Pack Details</h2>
              </div>
              <ShopProductFields
                form={form}
                onChange={patchForm}
                isUpdateMode={isUpdateMode}
                themePreferenceHelp='Optional. Usually left unset for sticker packs.'
              />
              {issues.length > 0 && (
                <div className='bg-red-500/10 border border-red-400/40 rounded-lg px-3 py-2 text-xs text-red-200 space-y-1'>
                  <div className='font-semibold'>Can't save yet:</div>
                  <ul className='list-disc pl-4 space-y-0.5'>
                    {issues.map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                onClick={handleSave}
                disabled={saving || deleting || issues.length > 0}
                className='w-full py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
              >
                {saving ? 'Saving...' : form.IsActive ? (isUpdateMode ? 'Update Sticker Pack' : 'Create Sticker Pack') : 'Save Draft (Inactive)'}
              </button>
              {isUpdateMode && (
                <button
                  onClick={handleDelete}
                  disabled={saving || deleting}
                  className='w-full py-2 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white transition disabled:opacity-60'
                >
                  {deleting ? 'Deleting...' : 'Delete Sticker Pack'}
                </button>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default StickerPackEditor;
